/**
 * Study status — single source of truth (API Title Case values).
 */

export const STUDY_STATUSES = {
  DRAFT: "Draft",
  ACTIVE: "Active",
  ARCHIVED: "Archived",
  /** Legacy / external CTMS Core statuses treated as assignable “active” studies. */
  RECRUITING: "Recruiting",
  ENROLLING: "Enrolling",
} as const;

export const STUDY_STATUS_VALUES = Object.values(STUDY_STATUSES);

export type StudyStatus = (typeof STUDY_STATUSES)[keyof typeof STUDY_STATUSES];

const canonicalize = (value: string): string =>
  value.toLowerCase().replace(/[\s/\-_]+/g, "");

const STATUS_BY_KEY: Record<string, StudyStatus> = Object.values(
  STUDY_STATUSES,
).reduce(
  (acc, value) => {
    acc[canonicalize(value)] = value;
    return acc;
  },
  {} as Record<string, StudyStatus>,
);

/** Resolve API/UI status string to StudyStatus, or null if unknown. */
export const tryNormalizeStudyStatus = (status: string): StudyStatus | null => {
  if (!status?.trim()) {
    return null;
  }
  if (STUDY_STATUS_VALUES.includes(status as StudyStatus)) {
    return status as StudyStatus;
  }
  return STATUS_BY_KEY[canonicalize(status)] ?? null;
};

/** Normalize to StudyStatus; unknown values default to Draft. */
export const normalizeStudyStatus = (status: string): StudyStatus =>
  tryNormalizeStudyStatus(status) ?? STUDY_STATUSES.DRAFT;

/** Strict compare: unknown status never matches (no Draft default). */
export const isStudyStatus = (status: string, target: StudyStatus): boolean =>
  tryNormalizeStudyStatus(status) === target;

export const isAnyStudyStatus = (
  status: string,
  ...targets: StudyStatus[]
): boolean => {
  const normalized = tryNormalizeStudyStatus(status);
  return normalized !== null && targets.includes(normalized);
};

/** Studies available for assignment / active operations. */
export const isAssignableStudyStatus = (status: string): boolean =>
  isAnyStudyStatus(
    status,
    STUDY_STATUSES.ACTIVE,
    STUDY_STATUSES.RECRUITING,
    STUDY_STATUSES.ENROLLING,
  );
